import { ChevronRight, Flame, FlaskConical, Sparkles } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { HeroCard } from "../components/HeroCard";
import { ProgressRing } from "../components/ProgressRing";
import { Reveal, SectionCard, SectionHeader } from "../components/Section";
import { JOY_BAR, JOY_TINT, JOY_VAR } from "../lib/joy";
import { ziggySrc, type ZiggyAge } from "../lib/ziggy";
import { useZiggyVariant } from "../lib/ziggyContext";
import {
  ACHIEVEMENT,
  ASSIGNMENT,
  EXPLORE,
  LEARNER,
  PROGRESS_BARS,
  PROGRESS_OVERALL,
  SCHEDULE,
} from "../lib/mockData";

export function StudentHome({
  firstName,
  ziggyAge = "teen",
}: {
  firstName: string;
  ziggyAge?: ZiggyAge;
}) {
  const { variant } = useZiggyVariant();
  return (
    <div className="space-y-5 lg:space-y-6">
      <HeroCard
        variant="student"
        title={<>Hi, {firstName}!</>}
        subtitle={`${LEARNER.className} · Section ${LEARNER.section}`}
        mascotSrc={ziggySrc(variant, ziggyAge, "happy")}
        mascotAlt="Ziggy waving hello"
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
          <Flame className="size-3.5" /> {LEARNER.streakDays}-day streak
        </span>
      </HeroCard>

      <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:[&>*]:min-w-0">
        {/* Today's schedule — horizontal rail */}
        <Reveal index={1} className="lg:col-span-2">
          <SectionHeader title="Today's Schedule" action="View all" />
          <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SCHEDULE.map((item) => (
              <div
                key={item.period}
                className="border-border/60 bg-card flex w-24 shrink-0 snap-start flex-col items-center gap-2 rounded-2xl border p-3 text-center shadow-[var(--shadow-card)]"
              >
                <span
                  className={cn("grid size-11 place-items-center rounded-xl", JOY_TINT[item.color])}
                >
                  <item.icon className="size-5" />
                </span>
                <span className="text-sm font-semibold leading-tight">{item.subject}</span>
                <span className="text-muted-foreground text-[0.7rem]">{item.time}</span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Upcoming assignment */}
        <Reveal index={2}>
          <SectionHeader title="Upcoming Assignment" action="View all" />
          <SectionCard className="flex items-center gap-4">
            <span
              className={cn("grid size-14 shrink-0 place-items-center rounded-2xl", JOY_TINT.green)}
            >
              <FlaskConical className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-semibold">{ASSIGNMENT.title}</p>
                <span className="bg-warning/15 text-warning shrink-0 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold">
                  {ASSIGNMENT.daysLeft} days left
                </span>
              </div>
              <p className="text-muted-foreground mt-0.5 truncate text-sm">
                Topic: {ASSIGNMENT.topic}
              </p>
              <p className="text-muted-foreground mt-0.5 text-xs">{ASSIGNMENT.dueLabel}</p>
            </div>
          </SectionCard>
        </Reveal>

        {/* Progress */}
        <Reveal index={3}>
          <SectionHeader title="My Progress" action="This term" />
          <SectionCard className="flex items-center gap-5">
            <ProgressRing
              value={PROGRESS_OVERALL}
              label={`${PROGRESS_OVERALL}%`}
              sublabel="Overall"
              color="var(--color-primary)"
            />
            <div className="flex-1 space-y-3">
              {PROGRESS_BARS.map((bar) => (
                <div key={bar.label}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{bar.label}</span>
                    <span
                      className="font-semibold tabular-nums"
                      style={{ color: JOY_VAR[bar.color] }}
                    >
                      {bar.value}%
                    </span>
                  </div>
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className={cn("h-full rounded-full transition-[width]", JOY_BAR[bar.color])}
                      style={{ width: `${bar.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </Reveal>

        {/* Explore */}
        <Reveal index={4} className="lg:col-span-2">
          <SectionHeader title="Explore" />
          <div className="grid grid-cols-4 gap-3">
            {EXPLORE.map((item) => (
              <button
                key={item.label}
                type="button"
                className="border-border/60 bg-card group flex flex-col items-center gap-2 rounded-2xl border p-3 text-center shadow-[var(--shadow-card)] transition-transform duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.97]"
              >
                <span
                  className={cn("grid size-11 place-items-center rounded-xl", JOY_TINT[item.color])}
                >
                  <item.icon className="size-5" />
                </span>
                <span className="text-[0.7rem] font-medium leading-tight">{item.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Achievements */}
        <Reveal index={5} className="lg:col-span-2">
          <div className="hero-celebrate hero-orbs relative overflow-hidden rounded-[1.5rem] p-5 text-white shadow-[var(--shadow-float)]">
            <div className="relative z-10 flex items-center gap-4">
              <img
                src={ziggySrc(variant, ziggyAge, "cheerful")}
                alt=""
                aria-hidden
                className="-my-2 h-24 w-20 shrink-0 object-contain drop-shadow-xl"
              />
              <div className="min-w-0 flex-1">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-[0.7rem] font-semibold backdrop-blur-sm">
                  <Sparkles className="size-3" /> {ACHIEVEMENT.badge}
                </span>
                <p className="font-display mt-1.5 text-lg font-semibold leading-tight">
                  {ACHIEVEMENT.headline}
                </p>
                <p className="mt-0.5 text-sm text-white/85">{ACHIEVEMENT.detail}</p>
              </div>
            </div>
            <button
              type="button"
              className="relative z-10 mt-4 flex w-full items-center justify-center gap-1 rounded-xl bg-white/20 py-2.5 text-sm font-semibold backdrop-blur-sm transition-transform duration-150 ease-out hover:bg-white/25 active:scale-[0.98]"
            >
              View my badges <ChevronRight className="size-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
